/* WebSocket-driven live feed for the admin dashboard.

Connects to /api/admin/live, expects JSON events of the shape published by
backend.app.services.live.emit_job_status. Keeps the most recent N events
in state and re-connects with backoff on disconnect.
*/
import { useEffect, useRef, useState } from "react";

const MAX_EVENTS = 50;

function buildWsUrl(path) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  // Same-origin (production) when REACT_APP_BACKEND_URL is empty
  const base = backend || `${window.location.protocol}//${window.location.host}`;
  return base.replace(/^http/, "ws") + path;
}

export function useLiveFeed() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(buildWsUrl("/api/admin/live"));
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (msg) => {
        try {
          const ev = JSON.parse(msg.data);
          setEvents((prev) => [
            { ...ev, _seq: Date.now() + Math.random() },
            ...prev,
          ].slice(0, MAX_EVENTS));
        } catch {
          // ignore malformed
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (cancelled) return;
        const delay = Math.min(15_000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        timer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  return { events, connected };
}
